const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

/** Iterative, seed-relative RGB selection. Inputs are never modified. */
export const findColorRegion = (rgba, width, height, erased, x, y, tolerance = 10, contiguous = true) => {
  x = Math.floor(x)
  y = Math.floor(y)
  const seed = y * width + x
  if (!Number.isFinite(seed) || x < 0 || y < 0 || x >= width || y >= height ||
      !rgba[seed * 4 + 3] || erased[seed]) return new Uint32Array()
  const limit = 3 * (255 * clamp(tolerance, 0, 100) / 100) ** 2
  const r = rgba[seed * 4]
  const g = rgba[seed * 4 + 1]
  const b = rgba[seed * 4 + 2]
  const matches = (i) => !erased[i] && rgba[i * 4 + 3] !== 0 &&
    (rgba[i * 4] - r) ** 2 + (rgba[i * 4 + 1] - g) ** 2 + (rgba[i * 4 + 2] - b) ** 2 <= limit
  const queue = new Uint32Array(width * height)
  let count = 0
  if (!contiguous) {
    for (let i = 0; i < queue.length; i++) {
      if (matches(i)) queue[count++] = i
    }
  } else {
    const visited = new Uint8Array(width * height)
    const visit = (i) => {
      if (visited[i]) return
      visited[i] = 1
      if (matches(i)) queue[count++] = i
    }
    visit(seed)
    for (let cursor = 0; cursor < count; cursor++) {
      const i = queue[cursor]
      if (i % width > 0) visit(i - 1)
      if (i % width < width - 1) visit(i + 1)
      if (i >= width) visit(i - width)
      if (i < width * (height - 1)) visit(i + width)
    }
  }
  // Own the exact-sized buffer, not a view retaining the full work queue.
  return queue.slice(0, count)
}

// Keep an oversized gesture alone so the latest change is always undoable.
export class TransparencyMask {
  constructor (rgba, width, height, historyBudget = 16 * 1024 * 1024) {
    this.width = width
    this.height = height
    this._rgba = new Uint8ClampedArray(rgba)
    this.mask = new Uint8Array(width * height)
    this._history = []
    this._position = 0
    this._budget = historyBudget
    this.historyBytes = 0
    this._erasedCount = 0
    this._stroke = null
    this._base = null
  }

  get canUndo () { return this._position > 0 }
  get canRedo () { return this._position < this._history.length }
  get hasErased () { return this._erasedCount > 0 }

  _record (indices, value) {
    if (!indices.length) return
    this._history.splice(this._position)
    this._history.push({ indices, value })
    this.historyBytes = this._history.reduce((sum, diff) => sum + diff.indices.byteLength, 0)
    while (this.historyBytes > this._budget && this._history.length > 1) {
      this.historyBytes -= this._history.shift().indices.byteLength
    }
    this._position = this._history.length
  }

  _apply (indices, value) {
    for (const i of indices) {
      this._erasedCount += value - this.mask[i]
      this.mask[i] = value
    }
  }

  wand (x, y, tolerance = 10, contiguous = true) {
    this.finishStroke()
    const indices = findColorRegion(this._rgba, this.width, this.height, this.mask, x, y, tolerance, contiguous)
    this._apply(indices, 1)
    this._record(indices, 1)
  }

  beginStroke () {
    this.finishStroke()
    this._stroke = new Uint8Array(this.mask.length)
    this._strokeCount = 0
  }

  /** A capsule around the segment fills gaps even when pointer events are sparse. */
  eraseLine (x0, y0, x1, y1, size = 20) {
    if (!this._stroke || ![x0, y0, x1, y1, size].every(Number.isFinite)) return
    const radius = clamp(size, 2, 120) / 2
    const left = Math.max(0, Math.ceil(Math.min(x0, x1) - radius))
    const right = Math.min(this.width - 1, Math.floor(Math.max(x0, x1) + radius))
    const top = Math.max(0, Math.ceil(Math.min(y0, y1) - radius))
    const bottom = Math.min(this.height - 1, Math.floor(Math.max(y0, y1) + radius))
    const dx = x1 - x0
    const dy = y1 - y0
    const lengthSq = dx * dx + dy * dy
    for (let y = top; y <= bottom; y++) {
      for (let x = left; x <= right; x++) {
        const i = y * this.width + x
        if (this.mask[i] || !this._rgba[i * 4 + 3]) continue
        const t = lengthSq ? clamp(((x - x0) * dx + (y - y0) * dy) / lengthSq, 0, 1) : 0
        if ((x - x0 - t * dx) ** 2 + (y - y0 - t * dy) ** 2 <= radius * radius) {
          this.mask[i] = this._stroke[i] = 1
          this._strokeCount++
          this._erasedCount++
        }
      }
    }
  }

  finishStroke () {
    if (!this._stroke) return
    const indices = new Uint32Array(this._strokeCount)
    let count = 0
    for (let i = 0; i < this._stroke.length; i++) {
      if (this._stroke[i]) indices[count++] = i
    }
    this._stroke = null
    this._record(indices, 1)
  }

  undo () {
    this.finishStroke()
    if (!this.canUndo) return
    const { indices, value } = this._history[--this._position]
    this._apply(indices, 1 - value)
  }

  redo () {
    this.finishStroke()
    if (!this.canRedo) return
    const { indices, value } = this._history[this._position++]
    this._apply(indices, value)
  }

  reset () {
    this.finishStroke()
    const indices = new Uint32Array(this._erasedCount)
    let count = 0
    for (let i = 0; i < this.mask.length; i++) {
      if (this.mask[i]) indices[count++] = i
    }
    this._apply(indices, 0)
    this._record(indices, 0)
  }

  /** The cached alpha base is never edited or returned directly (reset safety). */
  alphaMask (threshold = 128) {
    if (!this._base || this._threshold !== threshold) {
      this._threshold = threshold
      this._base = new Uint8Array(this.mask.length)
      for (let i = 0; i < this.mask.length; i++) {
        const alpha = this._rgba[i * 4 + 3]
        this._base[i] = alpha > 0 && alpha >= threshold ? 1 : 0
      }
    }
    const solid = this._base.slice()
    for (let i = 0; i < solid.length; i++) {
      if (this.mask[i]) solid[i] = 0
    }
    return solid
  }

  toRGBA () {
    const pixels = this._rgba.slice()
    for (let i = 0; i < this.mask.length; i++) {
      if (this.mask[i]) pixels[i * 4 + 3] = 0
    }
    return pixels
  }
}
